import React, { useEffect, useState } from 'react';
import { AvTooltip, Select, SpotlightSearch } from '@fabric-ui/components';
import { Apps, CommentFull as Comment } from '@fabric-ui/icons';
import { Explorer, QuestionFull } from '@fabric-ui/icons/app-icons';
import { SettingsFull as Settings } from '@fabric-ui/icons/screens';
import { APPBAR_HEIGHT, flex, shadowPrimaryBottomSm } from '@fabric-ui/theme';
import { iconSize, uniqBy } from '@fabric-ui/utils';
import { AppBar, Box, Button, buttonBaseClasses, Divider, Portal, SxProps, Toolbar, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@utils/AvRouter';
import { gql } from 'graphql-request';
import ZscalerLogo from '../../assets/zscaler_logo.png';
import { AccountType } from '../../context/AvContext.types';
import { useAvContext } from '../../context/AvContextProvider';
import { AppDto } from '../../context/types';
import { useAllApps } from '../../hooks/useAllApps';
import useKnowledgeBase from '../../hooks/useKnowledgeBase';
import useSalesforceSupportPortal from '../../hooks/useSalesforceSupportPortal';
import { APP_PATHS, AppPathsValue, PathFeatureFlag, PUBLIC_APPS } from '../../types';
import { App, SubMenuItem } from '../../types/sideBar.types';
import { appsOptions } from './appbar.constants';
import { appBarItemHoveredStyle, appBarItemSelectedStyle, appIconStyle, AppItem } from './AppItem';
import { appsSideBar } from './sidebar.constants';
import UserMenu from './UserMenu';

const useSetFav = () => {
  const { user, refetchAdditionalData, api } = useAvContext();
  return useMutation({
    mutationFn: ({ isFavorite, app }: { isFavorite: boolean; app: AppDto }) =>
      api(isFavorite ? ADD_TO_FAVORITE : REMOVE_FROM_FAVORITE, {
        options: {
          userId: user.userId,
          app,
        },
        onSuccess: refetchAdditionalData,
      }),
  });
};

interface SystemButtonProps {
  onClick: () => void;
  appBarItemStyle: SxProps;
  icon: React.JSX.Element;
  title?: string;
}
const AppBarSystemButton: React.FC<SystemButtonProps> = ({ onClick, appBarItemStyle, icon, title = '' }) => (
  <AvTooltip title={title}>
    <Button sx={appBarItemStyle} onClick={onClick}>
      {icon}
    </Button>
  </AvTooltip>
);

const AvAppBar = () => {
  const theme: any = useTheme();
  const {
    user,
    accountId,
    accountData,
    selectedApp,
    getPathName,
    api,
    userPermissions: { hasAllowedPermission },
    enabledApps,
    featureFlags,
  } = useAvContext();
  const openSalesforcePortal = useSalesforceSupportPortal({ api, currentApp: selectedApp });
  const navigateToKb = useKnowledgeBase({ newPage: true });
  const { data: AllApps = [] } = useAllApps();
  const { mutate } = useSetFav();
  const favAppsSystemName = (
    (user?.favoriteApps || []).map(app => appsOptions.find(({ enumValue }) => enumValue === app)).filter(Boolean) as App[]
  ).map(a => a.id);

  const [filteredAppsBarItems, setFilteredAppsBarItems] = useState(
    enabledApps.filter(app => favAppsSystemName.includes(app.id) || app.id === selectedApp)
  );

  const firstEnabledPathPerGlobalApp: Partial<Record<AppPathsValue, SubMenuItem | undefined>> = [
    APP_PATHS.PLATFORM,
    APP_PATHS.EXPLORE,
  ].reduce((acc, app) => {
    const firstItem = appsSideBar[app]
      .flatMap(s => s.subMenu)
      .find(menu => (!menu.ffForDisplaying || featureFlags[menu.ffForDisplaying]) && hasAllowedPermission({ path: menu.path, app }));
    return {
      ...acc,
      [app]: firstItem?.subMenu?.[0] ? { ...firstItem.subMenu[0], path: `${firstItem.path}/${firstItem.subMenu[0].path}` } : firstItem,
    };
  }, {});

  useEffect(() => {
    setFilteredAppsBarItems(
      uniqBy(
        [...filteredAppsBarItems, ...enabledApps].filter(app => favAppsSystemName.includes(app.id) || app.id === selectedApp),
        app => app.id
      )
    );
  }, [accountData.apps, selectedApp, user.favoriteApps]);

  const toggleFavoriteApp = (appClientId, isFavorite, e) => {
    e.preventDefault();
    e.stopPropagation();
    const matchingApp = appsOptions.find(({ id }) => id === appClientId)!;
    const app = AllApps.find(appDto => appDto.name === matchingApp.enumValue);
    if (app) {
      mutate({ isFavorite, app });
    }
  };

  const getAppBarItemLink = (app: App) => {
    const flattenMenus = (items: SubMenuItem[], parentPath?: string, isParentHidden?: boolean): SubMenuItem[] =>
      items.flatMap(item => {
        const isHiddenByFlag = !!(
          (item.ffForHiding && featureFlags[item.ffForHiding]) ||
          (item.ffForDisplaying && !featureFlags[item.ffForDisplaying])
        );
        const isHidden = isParentHidden || isHiddenByFlag;
        if (isHidden) {
          return [];
        }
        const combinedParentPath = parentPath ? `${parentPath}/${item.path}` : item.path;
        return item.subMenu?.length ? flattenMenus(item.subMenu, combinedParentPath, isHidden) : [{ ...item, parentPath }];
      });

    const firstAvailablePath = appsSideBar[app.id]
      .flatMap(menuItem => flattenMenus(menuItem.subMenu))
      .filter(
        menu => (!menu.ffForDisplaying || featureFlags[menu.ffForDisplaying]) && (!menu.ffForHiding || !featureFlags[menu.ffForHiding])
      )
      .sort((a, b) => (a?.landingPage ? 0 : 1) - (b?.landingPage ? 0 : 1))
      .find(menu => hasAllowedPermission({ path: menu?.path || '', app: app.id }));
    const resolvedPath = firstAvailablePath?.parentPath
      ? `${firstAvailablePath.parentPath}/${firstAvailablePath.path}`
      : firstAvailablePath?.path;
    return getPathName(resolvedPath, '', app.id).to;
  };

  return (
    <AppBar
      sx={{
        height: APPBAR_HEIGHT,
        zIndex: theme.zIndex.modal,
        background:
          'linear-gradient(174deg, var(--color-surface-nav-topBar-linearStart) 0%, var(--color-surface-nav-topBar-linearEnd) 43.826%)',
        boxShadow: shadowPrimaryBottomSm,
        gridArea: 'appbar',
        position: 'unset',
      }}>
      <Portal container={document.body}>
        <SpotlightSearch
          apps={enabledApps}
          appsMenuItems={appsSideBar}
          appPaths={APP_PATHS}
          pathFeatureFlag={PathFeatureFlag}
          publicApps={PUBLIC_APPS}
        />
      </Portal>
      <Toolbar
        sx={{
          height: APPBAR_HEIGHT,
          [theme.breakpoints.up('sm')]: {
            minHeight: APPBAR_HEIGHT,
          },
        }}>
        <Box sx={{ mr: 4, flexShrink: 0 }}>
          <Link to={accountId} from="/">
            <img alt="back to homepage logo" src={ZscalerLogo} style={{ height: 28, width: 127 }} />
          </Link>
        </Box>
        <Select
          label={<Apps style={{ color: 'var(--color-content-immutable-white)' }} />}
          showInput={false}
          renderOption={(app: any) => (
            <AppItem
              {...app}
              to={getAppBarItemLink(app)}
              appIconSize={24}
              showDescription
              toggleFavoriteApp={e => {
                toggleFavoriteApp(app.id, !favAppsSystemName.includes(app.id), e);
              }}
              isFavorite={favAppsSystemName.includes(app.id)}
              isDropdownItem
            />
          )}
          style={{
            [`&.${buttonBaseClasses.root}`]: {
              p: '8px',
              borderRadius: '12px',
              svg: {
                ...iconSize(22),
              },
              ...appBarItemHoveredStyle,
              '&.Mui-focusVisible': appBarItemSelectedStyle,
            },
          }}
          variant="text"
          transparent
          getValueFunc={({ id }) => id}
          getLabelFunc={({ name }) => name}
          showSelection={false}
          options={enabledApps}
          size="small"
          value={selectedApp}
        />
        <Divider flexItem orientation="vertical" sx={{ ...dividerBarItemStyle, pr: 1 }} />
        <Box sx={{ ...flex.itemsCenter, gap: '10px', pl: 2 }}>
          {filteredAppsBarItems.map(app => (
            <AppItem {...app} isSelected={app.id === selectedApp} to={getAppBarItemLink(app)} key={app.name} />
          ))}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ ...flex.itemsCenter, gap: '10px', pl: 1.25 }}>
          {firstEnabledPathPerGlobalApp[APP_PATHS.EXPLORE] && (
            <Link to={getPathName(firstEnabledPathPerGlobalApp[APP_PATHS.EXPLORE]!.path, '', APP_PATHS.EXPLORE).to}>
              <Box sx={{ ...appIconStyle(theme, selectedApp === APP_PATHS.EXPLORE, 26), pr: 1.75 }}>
                <Box sx={{ ...flex.center, py: '1px', height: 28, width: 28 }}>
                  <Explorer style={{ ...iconSize(22) }} />
                </Box>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Explore
                </Box>
              </Box>
            </Link>
          )}
          {firstEnabledPathPerGlobalApp[APP_PATHS.PLATFORM] && (
            <Link to={getPathName(firstEnabledPathPerGlobalApp[APP_PATHS.PLATFORM]!.path, '', APP_PATHS.PLATFORM).to}>
              <Box sx={{ ...appIconStyle(theme, selectedApp === APP_PATHS.PLATFORM, 26), pr: 1.75 }}>
                <Box sx={{ ...flex.center, py: '1px', height: 28, width: 28 }}>
                  <Settings style={iconSize(22)} />
                </Box>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Configure
                </Box>
              </Box>
            </Link>
          )}
        </Box>
        <Divider flexItem orientation="vertical" sx={{ ...dividerBarItemStyle, mx: 2 }} />
        <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
          <Box sx={{ ...flex.itemsCenter, gap: '5px' }}>
            <AppBarSystemButton
              title="Knowledge Center"
              onClick={navigateToKb}
              appBarItemStyle={appBarItemStyle(theme)}
              icon={<QuestionFull />}
            />
            {accountData.accountType !== AccountType.POC && (
              <AppBarSystemButton
                title="Contact Support"
                onClick={openSalesforcePortal}
                icon={<Comment />}
                appBarItemStyle={appBarItemStyle(theme)}
              />
            )}
          </Box>
          <Divider flexItem orientation="vertical" sx={dividerBarItemStyle} />
          <UserMenu setFilteredAppsBarItems={setFilteredAppsBarItems} />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AvAppBar;

const appBarItemStyle = (theme, isSelected?: boolean) => ({
  p: 1,
  borderRadius: '12px',
  border: `1px solid transparent`,
  transition: theme.transitions.create(['color'], {
    duration: theme.transitions.duration.shortest,
  }),
  color: 'var(--color-content-immutable-white)',
  svg: {
    ...iconSize(22),
  },
  ...appBarItemHoveredStyle,
  ...(isSelected && appBarItemSelectedStyle),
});

const dividerBarItemStyle = {
  height: 24,
  alignSelf: 'center',
  borderColor: 'var(--color-content-immutable-white)',
  opacity: 0.5,
};
const ADD_TO_FAVORITE = gql`
  mutation addFavoriteApp($userId: String!, $app: AppDtoInput!) {
    addFavoriteApp(userId: $userId, app: $app)
  }
`;

const REMOVE_FROM_FAVORITE = gql`
  mutation removeFavoriteApp($userId: String!, $app: AppDtoInput!) {
    removeFavoriteApp(userId: $userId, app: $app)
  }
`;
